import { useRef, type KeyboardEvent } from 'react';
import { Button, Card, ConfigProvider, Form, Input, Message, Typography } from '@arco-design/web-react';
import type { RefInputType } from '@arco-design/web-react/es/Input/interface';
import { IconLock, IconSafe, IconUser } from '@arco-design/web-react/icon';
import { useNavigate } from 'react-router-dom';
import { useAdminAuthStore } from '@/store/authStore';
import type { AdminLoginRequest } from '@/types/admin';
import './login.css';

/** 苹果绿主色 — 与客户台 Arco 默认蓝 #165DFF 区分 */
const ADMIN_PRIMARY = '#34C759';

export default function LoginView() {
  const navigate = useNavigate();
  const { login, loading } = useAdminAuthStore();
  const [form] = Form.useForm<AdminLoginRequest>();
  const passwordRef = useRef<RefInputType>(null);

  const onSubmit = async () => {
    if (loading) return;
    try {
      const values = await form.validate();
      await login(values);
      Message.success('登录成功');
      navigate('/dashboard');
    } catch (e) {
      if (e instanceof Error && !('isBusinessError' in e)) {
        // 校验失败的错误由 Form 自身提示，此处忽略
      }
    }
  };

  const focusPassword = () => {
    passwordRef.current?.focus();
  };

  const handleUsernameKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      focusPassword();
      return;
    }
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      focusPassword();
    }
  };

  const handlePasswordKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      void onSubmit();
    }
  };

  return (
    <ConfigProvider theme={{ primaryColor: ADMIN_PRIMARY }}>
      <div className="admin-login">
        <div className="admin-login__panel">
          <header className="admin-login__brand">
            <span className="admin-login__brand-badge">ADMIN</span>
            <h1 className="admin-login__brand-title">UP·CARD 管理后台</h1>
            <p className="admin-login__brand-sub">平台运营 · 客户与卡片管理 · 非客户登录入口</p>
          </header>

          <Card className="admin-login__card" bordered={false}>
            <Typography.Title heading={4} className="admin-login__card-title">
              <IconSafe style={{ marginRight: 8, color: ADMIN_PRIMARY }} />
              管理员登录
            </Typography.Title>
            <Typography.Text className="admin-login__card-desc">
              请使用<strong>管理员账号</strong> ，勿使用客户前台账号
            </Typography.Text>

            <Form
              form={form}
              layout="vertical"
              className="admin-login__form"
              onSubmit={onSubmit}
              size="large"
            >
              <Form.Item field="username" rules={[{ required: true, message: '请输入管理员账号' }]}>
                <Input
                  prefix={<IconUser />}
                  placeholder="管理员账号"
                  allowClear
                  autoFocus
                  onKeyDown={handleUsernameKeyDown}
                />
              </Form.Item>
              <Form.Item field="password" rules={[{ required: true, message: '请输入登录密码' }]}>
                <Input.Password
                  ref={passwordRef}
                  prefix={<IconLock />}
                  placeholder="登录密码"
                  onKeyDown={handlePasswordKeyDown}
                />
              </Form.Item>
              <Button
                type="primary"
                long
                loading={loading}
                htmlType="submit"
                className="admin-login__submit"
              >
                登录管理后台
              </Button>
            </Form>

            <p className="admin-login__hint">
              客户业务请访问 <strong>客户控制台</strong>（蓝色登录页）
            </p>
          </Card>
        </div>
      </div>
    </ConfigProvider>
  );
}
